/**
 * Landing page — first surface a coach hits when arriving via the
 * embed snippet (popup) or a shared link (redirect).
 *
 * Renders the list of events covered by this EmbedClient. Tapping an
 * event navigates to the EventPage where the divisions list and auth
 * gate live.
 *
 * For PR C (1/3) this is a thin shell pulling /embed/v1/config and
 * rendering the allowed_events list. The full flow components (auth
 * gate, team picker, confirm card, payment) get fleshed out across
 * later PRs.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { EmbedClient, ConfigResponse } from '@/api/embed';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { OrgLogo } from '@/components/OrgLogo';
import '@/pages/LandingPage.css';

interface LandingPageProps {
  // Read only to send a signed-in coach straight to their teams when
  // opening the bulk flow (skipping the sign-in fork).
  client?: EmbedClient;
  config: ConfigResponse | null;
}

export function GfgLandingPage({ client, config }: LandingPageProps) {
  const flowNavigate = useFlowNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // Deep-link targets carried on the initial URL by loader.js:
  //   ?event=EVT_ID          → jump straight to that event
  //   ?product=sponsorship   → open the sponsor flow (the "Sponsor this
  //                            event" button) instead of the product fork
  //   ?product=bulk          → bulk flow (data-product="bulk"): one team, one or
  //                            more events, starting from the event list
  const deepEvent = params.get('event');
  const deepProduct = params.get('product');

  useEffect(() => {
    if (!config) return;
    const dest = (eventId: string) =>
      deepProduct === 'sponsorship'
        ? `/events/${eventId}/sponsor`
        : deepProduct === 'register'
          ? `/events/${eventId}/register`
          : `/events/${eventId}`;

    // Bulk is only reached from a data-product="bulk" snippet and always
    // starts from the event list (a single-event button uses 'register').
    if (deepProduct === 'bulk') {
      flowNavigate(client?.isAuthed() ? '/bulk/teams' : '/bulk/auth', { replace: true });
      return;
    }
    if (deepEvent) {
      flowNavigate(dest(deepEvent), { replace: true });
      return;
    }
    // If only one event is configured for this snippet, skip the landing
    // page and go straight to it. Common case: organizer pastes a
    // per-event snippet on a per-event page.
    if (config.allowed_events.length === 1) {
      flowNavigate(dest(config.allowed_events[0].id), { replace: true });
    }
  }, [client, config, deepEvent, deepProduct, flowNavigate]);

  if (!config) {
    return <div className="tn-landing__loading">Loading…</div>;
  }

  if (config.allowed_events.length === 0) {
    return (
      <div className="tn-card tn-landing__empty">
        <h2>No events available</h2>
        <p>
          This registration link doesn't have any events configured yet.
          Please check back with the organizer.
        </p>
      </div>
    );
  }

  const orgName = config.branding.organizer_name;
  return (
    <div className="tn-landing">
      {/* Next skin: who you're registering with, big, on the first screen
          (the header lockup takes over from the next step). */}
      {orgName ? (
        <div className="tn-landing__brand">
          <OrgLogo branding={config.branding} className="tn-landing__brand-logo" />
          <span className="tn-landing__brand-name">{orgName}</span>
        </div>
      ) : null}
      <h1 className="tn-landing__title">Choose an event</h1>
      <p className="tn-landing__subtitle">
        Pick the event you want to take part in.
      </p>
      <ul className="tn-landing__events">
        {config.allowed_events.map((event) => (
          <li key={event.id}>
            <button
              type="button"
              className="tn-landing__event"
              // Warm the division screen while the finger/mouse is on its way.
              onPointerEnter={() => event.has_registration && client?.prefetchEvent(event.id)}
              onPointerDown={() => event.has_registration && client?.prefetchEvent(event.id)}
              onFocus={() => event.has_registration && client?.prefetchEvent(event.id)}
              onClick={() => {
                setLoading(true);
                flowNavigate(`/events/${event.id}`);
              }}
              disabled={loading}
            >
              <span className="tn-landing__event-name">{event.name}</span>
              <span className="tn-landing__event-arrow" aria-hidden>→</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
